/**
 * Insight Service - API client for insight extraction
 * TASK-XXX: Separates insight extraction from Search System
 *
 * This service calls the Meeting Orchestrator's insight extraction endpoint
 * instead of relying on insights from the search response.
 */

import { httpClient } from '../lib/httpClient';
import type { SearchInsight } from '../types/search.types';

/**
 * Request to extract insights from search results
 */
interface InsightExtractionRequest {
  query: string;
  resultsText: string;
  sourceIds: string[];
  maxInsights?: number;
}

/**
 * Response from insight extraction endpoint
 */
interface InsightExtractionResponse {
  insights: SearchInsight[];
  extractionTimeMs: number;
  success: boolean;
  error?: string;
}

/**
 * Raw insight from API (snake_case)
 */
interface RawInsight {
  title: string;
  description: string;
  category: string;
  importance: string;
  confidence: number;
  source_ids: string[];
}

/**
 * Raw response from API (snake_case)
 */
interface RawInsightResponse {
  insights: RawInsight[];
  extraction_time_ms: number;
  success: boolean;
  error?: string;
}

class InsightService {
  /**
   * Extract structured insights from search results.
   *
   * Calls the Meeting Orchestrator's insight extraction endpoint
   * which uses LLM to analyze search results and extract meeting-prep insights.
   *
   * @param request - Query, results text, and source IDs
   * @param signal - Optional AbortSignal for cancellation
   * @returns Extracted insights with metadata
   */
  async extractInsights(
    request: InsightExtractionRequest,
    signal?: AbortSignal
  ): Promise<InsightExtractionResponse> {
    try {
      const data = await httpClient.post<RawInsightResponse>('/api/insights/extract', {
        query: request.query,
        results_text: request.resultsText,
        source_ids: request.sourceIds,
        max_insights: request.maxInsights || 10,
      }, { signal });

      return {
        insights: (data.insights || []).map((i) => ({
          title: i.title,
          description: i.description,
          category: i.category as SearchInsight['category'],
          importance: i.importance as SearchInsight['importance'],
          confidence: i.confidence,
          sourceIds: i.source_ids || [],
        })),
        extractionTimeMs: data.extraction_time_ms,
        success: data.success,
        error: data.error,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }

      const message = error instanceof Error ? error.message : 'Insight extraction failed';
      return {
        insights: [],
        extractionTimeMs: 0,
        success: false,
        error: message,
      };
    }
  }
}

// Export singleton instance
export const insightService = new InsightService();
export default insightService;
